import { cn } from '@kidshome/ui';
import { spotArt } from '@/assets/icons';

/**
 * Renders a Register-B spot illustration (docs/brand/icon-storage.md, Home 2) by its SEMANTIC key, with a
 * graceful EMOJI fallback while the art is still being produced — so a surface is never blank and we can
 * migrate emoji → art one key at a time.
 *
 * Art files live in `src/assets/illustrations/<group>/<name>.svg` (Vite content-hashes them at build →
 * cache-busted + precached offline by the existing PWA glob) and are wired into `registry.ts`. Until a key
 * has art, `fallbackGlyph` renders. Dark / Cozy-Pastel variant selection is intentionally deferred to the
 * registry+resolver until the first real art lands (see the doc) — keep callers stable.
 *
 * Decorative by default (`aria-hidden`); pass `alt` (an already-translated i18n string) only when the image
 * is the SOLE carrier of meaning.
 */
export function Illustration({
  name,
  alt,
  fallbackGlyph = '🍃',
  size = 96,
  className,
}: {
  name: string;
  alt?: string;
  fallbackGlyph?: string;
  size?: number;
  className?: string;
}): React.ReactElement {
  const src = spotArt(name);

  if (!src) {
    return (
      <span
        className={cn('inline-block leading-none', className)}
        style={{ fontSize: size * 0.6 }}
        role={alt ? 'img' : undefined}
        aria-label={alt}
        aria-hidden={alt ? undefined : true}
      >
        {fallbackGlyph}
      </span>
    );
  }

  return (
    <img
      src={src}
      width={size}
      height={size}
      className={className}
      loading="lazy"
      decoding="async"
      alt={alt ?? ''}
      aria-hidden={alt ? undefined : true}
    />
  );
}
